import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Lock, ArrowRight, CheckCircle2, Shield } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"

export default function PublicSmartLink() {
  const { slug } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);
  const ownerKey = useMemo(() => (meta?.ownerId ? String(meta.ownerId) : null), [meta?.ownerId])
  const lsGuestKey = useMemo(() => (ownerKey ? `guest:${ownerKey}` : null), [ownerKey])
  const lsFanKey = useMemo(() => (ownerKey ? `fan:${ownerKey}` : null), [ownerKey])
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [autoRedirecting, setAutoRedirecting] = useState(false);
  // Use the same E.164 phone regex as in Fans.jsx
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;

  // Optional campaign ID embedded in the URL as ?campaign=...
  const campaignId = useMemo(() => {
    try {
      const qs = new URLSearchParams(location.search || '');
      return qs.get('campaign') || null;
    } catch { return null }
  }, [location.search]);

  // Track dwell time for campaign average time spent
  const mountTsRef = useRef(Date.now());
  const dwellMs = () => Math.max(0, Date.now() - (mountTsRef.current || Date.now()));

  // Per-visit click guard: ensure we only send one click per owner+slug per tab/session
  const markClickSent = (ownerId, slug) => {
    try {
      if (!ownerId || !slug) return false
      const key = `click:${ownerId}:${slug}`
      if (sessionStorage.getItem(key)) return false
      sessionStorage.setItem(key, '1')
      return true
    } catch {
      return true // if sessionStorage is unavailable, allow sending
    }
  }

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
  const headers = { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' };
  const res = await fetch(`https://backend.amply.buildfast.us/smart-links/resolve/${slug}`, { headers });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Link not found');
        if (mounted) setMeta(data.data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => { mounted = false };
  }, [slug]);

  // On first visit per owner, if no per-owner guest id, use global 'Guest' if present; otherwise create guest and store
  useEffect(() => {
    if (!meta?.ownerId || !slug) return;
    try {
      const existingPerOwnerGuest = lsGuestKey ? localStorage.getItem(lsGuestKey) : null
      if (existingPerOwnerGuest || !lsGuestKey) return

      // Prefer global 'Guest' id if exists
      const globalGuest = localStorage.getItem('Guest')
      const guestId = globalGuest || Math.random().toString(36).slice(2, 10)

      // Fire and forget guest creation for this owner
  fetch('https://backend.amply.buildfast.us/smart-links/guest', {
        method: 'POST',
  headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
        body: JSON.stringify({ ownerId: meta.ownerId, slug, guestId })
      }).catch(()=>{})

      // Persist both global and per-owner keys
      localStorage.setItem('Guest', guestId)
      localStorage.setItem(lsGuestKey, guestId)
    } catch {}
  }, [meta?.ownerId, lsGuestKey, slug])

  // For capture-required pages, when a guest visits (not already a fan), ensure we
  // record a click for both the smart link and the guest exactly once per visit.
  // Avoid double-counting on auto-redirect paths handled elsewhere.
  const guestVisitClickedRef = useRef(false)
  useEffect(() => {
    if (!meta?.ownerId || !slug || autoRedirecting) return
    const hasFan = lsFanKey ? localStorage.getItem(lsFanKey) : null
    if (hasFan) return
    const needEmail = !!meta.requireEmail
    const needPhone = !!meta.requirePhone
    const requiresCapture = needEmail || needPhone
    if (!requiresCapture) return
    if (guestVisitClickedRef.current) return

    guestVisitClickedRef.current = true
    ;(async () => {
      try {
        // Ensure we have a stable guestId for this owner (and globally)
        let gid = lsGuestKey ? localStorage.getItem(lsGuestKey) : null
        if (!gid) {
          const globalGuest = localStorage.getItem('Guest')
          gid = globalGuest || Math.random().toString(36).slice(2, 10)
          if (lsGuestKey) localStorage.setItem(lsGuestKey, gid)
          if (!globalGuest) localStorage.setItem('Guest', gid)
        }

        // Ensure guest exists on backend (idempotent), then record click
        try {
          await fetch('https://backend.amply.buildfast.us/smart-links/guest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
            body: JSON.stringify({ ownerId: meta.ownerId, slug, guestId: gid })
          })
        } catch {}

        if (markClickSent(meta.ownerId, slug)) {
          try {
              await fetch('https://backend.amply.buildfast.us/smart-links/click', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
              body: JSON.stringify({ slug, ownerId: meta.ownerId, guestId: gid, campaignId, dwellMs: dwellMs() })
            })
          } catch {}
        }
      } catch {}
    })()
  }, [meta?.ownerId, meta?.requireEmail, meta?.requirePhone, slug, autoRedirecting, lsFanKey, lsGuestKey])

  // If no capture required, immediately record lead and redirect
  useEffect(() => {
    if (loading || !meta || autoRedirecting) return;
    const hasFan = lsFanKey ? localStorage.getItem(lsFanKey) : null
    // If user already a fan for this owner, bypass forms entirely
    if (hasFan) {
      setAutoRedirecting(true)
      ;(async () => {
        try {
          // Record click for known fan, then proceed to lead/redirect
          if (markClickSent(meta.ownerId, slug)) {
            try {
              await fetch('https://backend.amply.buildfast.us/smart-links/click', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
                body: JSON.stringify({ slug, ownerId: meta.ownerId, fanId: hasFan, campaignId, dwellMs: dwellMs() })
              })
            } catch {}
          }
          const res = await fetch('https://backend.amply.buildfast.us/smart-links/lead', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
            body: JSON.stringify({ slug, name: null, email: null, phone: null, fanId: hasFan, auto: true, campaignId })
          })
          const data = await res.json()
          if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to submit')
          window.location.assign(data.redirectTo)
        } catch (e) {
          setError(e.message)
          setAutoRedirecting(false)
        }
      })()
      return
    }

    // If no capture is required, record a lead (without creating a fan) and redirect
    const needEmail = !!meta.requireEmail;
    const needPhone = !!meta.requirePhone;
    if (!needEmail && !needPhone) {
      setAutoRedirecting(true);
      (async () => {
        try {
          // If this is likely a guest/no-capture redirect, record a click as guest
          const guestId = lsGuestKey ? localStorage.getItem(lsGuestKey) : null
          if (markClickSent(meta.ownerId, slug)) {
            try {
              await fetch('https://backend.amply.buildfast.us/smart-links/click', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
                body: JSON.stringify({ slug, ownerId: meta.ownerId, guestId, campaignId, dwellMs: dwellMs() })
              })
            } catch {}
          }
          const res = await fetch('https://backend.amply.buildfast.us/smart-links/lead', {
      method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
            body: JSON.stringify({ slug, name: null, email: null, phone: null, campaignId })
          });
          const data = await res.json();
          if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to submit');
          window.location.assign(data.redirectTo);
        } catch (e) {
          setError(e.message);
          setAutoRedirecting(false);
        }
      })();
    }
  }, [loading, meta, slug, autoRedirecting, lsFanKey]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!meta) return;
    setSubmitting(true);
    try {
      // Enforce E.164 phone format when phone is required
      const needPhone = !!meta.requirePhone;
      if (needPhone && !phoneRegex.test(phone)) {
        setError('Invalid phone number format. Use E.164, e.g., +15551234567.');
        setSubmitting(false);
        return;
      }
      const existingFan = lsFanKey ? localStorage.getItem(lsFanKey) : null
  const res = await fetch('https://backend.amply.buildfast.us/smart-links/lead', {
    method: 'POST',
  headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'https://backend.amply.buildfast.us', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': 'Content-Type' },
  body: JSON.stringify({ slug, name, email, phone, fanId: existingFan, campaignId })
  });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to submit');
      // If backend created or confirmed a fan, persist fanId locally for this owner
      if (data.fanId && lsFanKey) {
        localStorage.setItem(lsFanKey, data.fanId)
      }
      window.location.assign(data.redirectTo);
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

 if (loading || autoRedirecting) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
      <div className="text-center">
        <div role="status" className="flex justify-center mb-4">
          <svg
            className="w-12 h-12 text-blue-600 animate-spin dark:text-blue-400"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 91.871 24.924 87.211 21.121C81.965 15.6745 75.572 11.723 69.474 8.92207C63.376 6.12107 57.049 4.41343 50.574 3.77569C44.099 3.13793 37.75 3.68757 31.7096 5.39926C25.6692 7.11095 19.9198 9.99924 14.5785 14.1271C9.23701 19.255 5.00913 25.6758 2.52655 32.809C0.0440134 39.9421 0.352616 47.4645 3.14775 54.7516C5.94293 62.0387 10.2853 68.9216 16.3023 74.3851C22.3192 79.8486 29.6815 84.1988 37.656 87.0095C45.6305 89.8202 54.1757 91.107 63.031 91.107C71.8863 91.107 80.4315 89.8202 88.406 87.0095C96.3805 84.1988 103.743 79.8486 109.76 74.3851C115.777 68.9216 120.12 62.0387 122.915 54.7516C125.71 47.4645 126.018 39.9421 123.536 32.809"
              fill="currentFill"
            />
          </svg>
        </div>
        <p className="text-lg font-medium text-gray-600 dark:text-gray-300">
          {autoRedirecting ? 'Redirecting…' : 'Loading…'}
        </p>
      </div>
    </div>
  );
}

if (error) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
      <div className="text-center p-8 max-w-md mx-auto">
        <div className="w-24 h-24 mx-auto mb-6 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
          <svg
            className="w-12 h-12 text-red-600 dark:text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1 className="text-6xl font-bold text-gray-900 dark:text-white mb-2">Oops!</h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 mb-6">
          Something went wrong.
        </p>
        <p className="text-gray-500 dark:text-white mb-8">{error}</p>
        <button
          onClick={() => window.location.replace('./')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
        >
          Back Home
        </button>
      </div>
    </div>
  );
}
  if (!meta) return null;

  const needEmail = !!meta.requireEmail;
  const needPhone = !!meta.requirePhone;

  return (
   <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent/20 to-accent/10 rounded-2xl mb-4 shadow-lg shadow-accent/10">
            <Lock className="w-8 h-8 text-accent" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2 text-balance tracking-tight">Access {meta.title}</h1>
          <p className="text-muted-foreground text-sm flex items-center justify-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            Secured by <span className="font-semibold text-accent">Amply</span>
          </p>
        </div>

        <Card className="border-0 shadow-2xl bg-card/90 backdrop-blur-md border-[#ebcebc] border-2">
          <CardHeader className="space-y-3 pb-6">
            <CardTitle className="text-xl font-semibold text-card-foreground">Unlock Content</CardTitle>
            <CardDescription className="text-muted-foreground leading-relaxed">
              Please provide your details to access this exclusive content.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-card-foreground flex items-center gap-2">
                  Name
                  <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-muted/50 border-[#ebcebc] border-2 ">
                    Optional
                  </Badge>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 bg-input/50 border-border/60 focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all duration-300 hover:border-border"
                  placeholder="Enter your name"
                />
              </div>

              {needEmail && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-card-foreground flex items-center gap-2">
                    Email Address
                    <Badge variant="destructive" className="text-xs px-2 py-0.5 bg-destructive/90 border-[#ff0000] border-2 ">
                      Required
                    </Badge>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 bg-input/50 border-border/60 focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all duration-300 hover:border-border"
                    placeholder="your@email.com"
                  />
                </div>
              )}

              {needPhone && (
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-card-foreground flex items-center gap-2">
                    Phone Number
                    <Badge variant="destructive" className="text-xs px-2 py-0.5 bg-destructive/90 border-[#ff0000] border-2 ">
                      Required
                    </Badge>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    pattern={phoneRegex.source}
                    title="Enter a valid E.164 phone number (e.g., +15551234567)"
                    inputMode="tel"
                    autoComplete="tel"
                    className="h-12 bg-input/50 border-border/60 focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all duration-300 hover:border-border"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              )}

              {!needEmail && !needPhone && (
                <div className="flex items-start gap-3 p-4 bg-accent/10 border border-accent/30 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-card-foreground">No additional details required</p>
                    <p className="text-xs text-muted-foreground mt-1">You can proceed directly to your content.</p>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group shadow-lg shadow-accent/20 hover:shadow-xl hover:shadow-accent/30 border-[#ebcebc] border-2"
              >
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                    Processing...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    Continue to Content
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200 " />
                  </div>
                )}
              </Button>
            </form>

            <div className="pt-4 border-t border-border/50">
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <p className="text-center leading-relaxed">
                  Your information is secure and will only be used to provide access to the requested content.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
